import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { type ComponentProps, useCallback, useEffect, useState } from 'react';

import type { QueryParams, SetActiveIdCallback } from '@/pages/Notifications/notifications.types';

import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import {
    fetchSimilarItemAlerts,
    toggleAllSimilarItemAlertsEmail,
} from '@/redux/modules/similarItemAlerts/similarItemAlerts.actions';
import {
    getSimilarItemAlertsListItems,
    getSimilarItemAlertsLotIds,
    getSimilarItemAlertsTotalCount,
    isSimilarItemAlertsListEmpty,
} from '@/redux/modules/similarItemAlerts/similarItemAlerts.selectors';
import { DEFAULT_SIMILAR_ITEM_ALERTS_PAGE_SIZE } from '@/redux/modules/similarItemAlerts/similarItemAlerts.types';
import { useSimilarItemAlertsExperiment } from '@/utils/amplitude/experiments/hooks/useSimilarItemAlerts.experiment';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { Section } from './Components/Section';
import { SimilarItemAlertsTable } from './Components/SimilarItemAlertsTable';
import { StatusCheckbox } from './Components/StatusCheckbox';

type Props = {
    activeId: QueryParams['section'];
    setActiveId: SetActiveIdCallback;
};

const PAGE_SIZE = DEFAULT_SIMILAR_ITEM_ALERTS_PAGE_SIZE;

/**
 * The "Similar Item Alerts" section on Emails & Notifications (SEAR-1647). Experiment-gated — control
 * never renders it. Lists the bidder's alerts (savedsearch-api `GET /saved-items`), hydrating each
 * lotId into a card via item-summaries, with per-row + section-level email toggles and a delete flow
 * that opens the shared confirm + feedback modals.
 */
export const SimilarItemAlertsSection = ({ activeId, setActiveId }: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const hasSimilarItemAlerts = useSimilarItemAlertsExperiment();

    const alerts = useAppSelector(getSimilarItemAlertsListItems);
    const totalCount = useAppSelector(getSimilarItemAlertsTotalCount);
    const isEmpty = useAppSelector(isSimilarItemAlertsListEmpty);
    const lotIds = useAppSelector(getSimilarItemAlertsLotIds);

    const [page, setPage] = useState(1);

    useEffect(() => {
        if (hasSimilarItemAlerts) {
            dispatch(fetchSimilarItemAlerts({ page, pageSize: PAGE_SIZE, sort: 'savedNewest' }));
        }
    }, [dispatch, hasSimilarItemAlerts, page]);

    // Hydrate the lot ids into the shared item-summary slice so each row can render its title and
    // thumbnail. A lot that can't be hydrated still shows via the row's stored lotTitle.
    useEffect(() => {
        if (lotIds.length) {
            dispatch(fetchItemSummariesIfNeeded({ identifier: 'SIMILAR-ITEM-ALERTS', itemIds: lotIds }));
        }
    }, [dispatch, lotIds]);

    const onToggleAll = useCallback(
        (checked: boolean) => {
            dispatch(toggleAllSimilarItemAlertsEmail({ sendAlert: !checked }));
        },
        [dispatch]
    );

    const onPageChange = useCallback<ComponentProps<typeof Pagination>['onChange']>(({ page }) => {
        setPage(page);
    }, []);

    if (!hasSimilarItemAlerts) {
        return null;
    }

    const showPager = totalCount > PAGE_SIZE;

    const table = <SimilarItemAlertsTable alerts={alerts} />;

    return (
        <Section
            activeId={activeId}
            component={
                <div className="mb-xs mr-auto mt-0 block w-full max-w-[1200px] self-center border-b border-border-secondary pb-lg">
                    {isEmpty ? (
                        <div className="mt-md rounded-md bg-background-subdued p-lg text-center">
                            <BodyPrimary className="block font-bold">
                                {formatMessage(['similarItemAlerts', 'emptyTitle'])}
                            </BodyPrimary>
                            <BodySecondary className="mt-2xs block">
                                {formatMessage(['similarItemAlerts', 'emptyBody'])}
                            </BodySecondary>
                        </div>
                    ) : showPager ? (
                        <Pagination
                            onChange={onPageChange}
                            paginationFilter={{ page, pageSize: PAGE_SIZE, totalRecords: totalCount }}
                        >
                            {table}
                        </Pagination>
                    ) : (
                        table
                    )}
                </div>
            }
            count={totalCount}
            email={
                <StatusCheckbox
                    array={alerts}
                    category="similar item alert"
                    onChange={onToggleAll}
                    selector={(alert) => alert.sendAlert}
                    type="email"
                />
            }
            id="similarItemAlerts"
            setActiveId={setActiveId}
        />
    );
};
